import { sortBy } from "lodash-es";

import {
  createAppSelector,
  selectOutcomeGroups,
  selectOutcomeSubgroups,
  selectOutcomes,
  selectPageOutcomes,
  selectSectionOutcomes,
} from "./base";
import { selectCurrentOrg } from "./org";
import { selectSectionsForCurrentUser } from "./section";
import {
  NestedOutcomeGroupType,
  NestedOutcomeSubgroupType,
  NestedPageOutcomeType,
  NestedSectionOutcomeType,
} from "./types";

import { OrgType, OutcomeItemType, RubricType } from "components/server-types";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";

export const selectNestedOutcomeGroups = createAppSelector(
  [selectOutcomeGroups, selectOutcomeSubgroups, selectOutcomes],
  (outcomeGroups, outcomeSubgroups, outcomes) => {
    return outcomeGroups.map((outcomeGroup) => {
      let outcomeCount = 0;

      const filteredOutcomes = outcomes.filter(
        (outcome) => outcome.outcome_group_id === outcomeGroup.id,
      );
      outcomeCount += filteredOutcomes.length;

      const filteredOutcomeSubgroups = outcomeSubgroups
        .filter((outcomeSubgroup) => outcomeSubgroup.outcome_group_id === outcomeGroup.id)
        .map<NestedOutcomeSubgroupType>((outcomeSubgroup) => {
          const filteredSubgroupOutcomes = outcomes.filter(
            (outcome) => outcome.outcome_subgroup_id === outcomeSubgroup.id,
          );

          outcomeCount += filteredSubgroupOutcomes.length;

          return {
            ...outcomeSubgroup,
            outcomes: filteredSubgroupOutcomes,
          };
        });

      return {
        ...outcomeGroup,
        outcomeSubgroups: filteredOutcomeSubgroups,
        outcomes: filteredOutcomes,
        outcomeCount,
      } as NestedOutcomeGroupType;
    });
  },
);

export const selectSectionOutcomesForSection = createAppSelector(
  [(state, sectionId: string) => sectionId, selectSectionOutcomes],
  (sectionId, section_outcomes) =>
    section_outcomes.filter((section_outcome) => section_outcome.section_id === sectionId),
);

export const selectFullSectionOutcomes = createAppSelector(
  [
    (state, sectionId: string | null) => sectionId,
    selectSectionOutcomes,
    (state) => state.outcomes,
  ],
  (sectionId, section_outcomes, outcomes) => {
    const nestedSectionOutcomes = section_outcomes
      .filter((section_outcome) => section_outcome.section_id === sectionId)
      .reduce((acc, section_outcome) => {
        const outcome = outcomes[section_outcome.outcome_id];
        if (outcome) {
          acc.push({
            ...section_outcome,
            outcome,
          } as NestedSectionOutcomeType);
        }
        return acc;
      }, [] as NestedSectionOutcomeType[]);
    return sortBy(nestedSectionOutcomes, (co) => co.outcome.title.toLowerCase());
  },
);

export const selectOrgOutcomesForSection = createAppSelector(
  [selectFullSectionOutcomes, selectOutcomes],
  (sectionOutcomes) => {
    const filteredSectionOutcomes = sectionOutcomes.filter(
      (sectionOutcome) => !!sectionOutcome.outcome,
    );
    return filteredSectionOutcomes.map((sectionOutcome) => sectionOutcome.outcome);
  },
);

export const selectNestedPageOutcomes = createAppSelector(
  [
    (state, pageId: string) => pageId,
    selectPageOutcomes,
    (state) => state.section_outcomes,
    (state) => state.outcomes,
  ],
  (pageId, page_outcomes, section_outcomes, outcomes) => {
    const nestedPageOutcomes = page_outcomes
      .filter((page_outcome) => page_outcome.page_id === pageId)
      .reduce((acc, page_outcome) => {
        const sectionOutcome = section_outcomes[page_outcome.section_outcome_id];
        const outcome = outcomes[sectionOutcome?.outcome_id];
        if (sectionOutcome && outcome) {
          acc.push({
            ...page_outcome,
            outcome,
            section_outcome: sectionOutcome,
          } as NestedPageOutcomeType);
        }
        return acc;
      }, [] as NestedPageOutcomeType[]);
    return sortBy(nestedPageOutcomes, (po) => po.outcome.title.toLowerCase());
  },
);

export const selectOrgOutcomeRubric = createAppSelector(
  [
    (state, outcomeId: string | undefined) => outcomeId,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (outcomeId, outcomes, org) => {
    if (!outcomeId || !org) return null;
    const outcome = outcomes[outcomeId];
    if (!outcome) return null;
    return determineRubric(org, [outcome]);
  },
);

export const selectSectionOutcomeRubric = createAppSelector(
  [
    (state, sectionOutcomeId: string) => sectionOutcomeId,
    (state) => state.section_outcomes,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (sectionOutcomeId, section_outcomes, outcomes, org) => {
    const section_outcome = section_outcomes[sectionOutcomeId];
    const outcome = outcomes[section_outcome?.outcome_id];
    if (!org || !section_outcome || !outcome) return null;
    return determineRubric(org, [section_outcome, outcome]);
  },
);

export const selectPageOutcomeRubric = createAppSelector(
  [
    (state, pageOutcomeId: string) => state.page_outcomes[pageOutcomeId],
    (state) => state.section_outcomes,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (page_outcome, section_outcomes, outcomes, org) => {
    const section_outcome = section_outcomes[page_outcome?.section_outcome_id];
    const outcome = outcomes[section_outcome?.outcome_id];
    if (!org || !page_outcome || !section_outcome || !outcome) return null;
    return determineRubric(org, [page_outcome, section_outcome, outcome]);
  },
);

const determineRubric = (org: OrgType, outcomeItems: OutcomeItemType[]): RubricType => {
  // Create a new rubric object by copying the default rubric for the org, then
  // potentially substituting rubric descriptions from the first supplied OutcomeItem
  // argument that has customized rubric descriptions, if any.
  const outcomeItem = outcomeItems.find((item) => !!item.rubric_descriptions);
  const customDescriptions = outcomeItem && outcomeItem.rubric_descriptions;
  return org.rubric.map((rubricLevel, index) => ({
    ...rubricLevel,
    description: customDescriptions ? customDescriptions[index] : rubricLevel.description,
  }));
};

export const selectPageOutcomesByPageId = createAppSelector(
  [selectPageOutcomes, (state, pageId: string) => pageId],
  (pageOutcomes, pageId) => pageOutcomes.filter((po) => po.page_id === pageId),
);

export const selectOutcomesForCurrentUser = createAppSelector(
  [selectSectionsForCurrentUser, selectSectionOutcomes, (state) => state.outcomes],
  (sections, sectionOutcomes, outcomeMap) => {
    const sectionIdsForUser = new Set(sections.map((section) => section.id));
    const sectionOutcomesForUser = sectionOutcomes.filter((sectionOutcome) =>
      sectionIdsForUser.has(sectionOutcome.section_id),
    );
    // We need to get the outcomes from the section outcomes, but we could get duplicates.
    // We can use a Set to ensure that we only include each outcome ID once.
    const outcomeIdsForUser = new Set(
      sectionOutcomesForUser.map((sectionOutcome) => sectionOutcome.outcome_id),
    );
    return Array.from(outcomeIdsForUser)
      .map((outcomeId) => outcomeMap[outcomeId])
      .filter(Boolean)
      .sort(ORDER_BY_TITLE);
  },
);
